import { Navigation } from './components'
import { Button, Modal, Title, Table, Checkbox, Stack, Text, Group } from '@mantine/core'
import { IconPlus, IconDownload, IconTrash } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { formatDateTime } from '@/utils.ts'
import InviteCallCenterAgent from './components/InviteCallCenterAgent'
import { SNOMED_USER_ROLES } from '@/utils'
import { formatHumanName } from '@medplum/core'
import { Practitioner, Resource } from '@medplum/fhirtypes'
import { normalizeErrorString } from '@medplum/core'

export function CallCenterAgents() {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [agentToDelete, setAgentToDelete] = useState<Practitioner | null>(null)
  const medplum = useMedplum()
  const [callCenterAgents, setCallCenterAgents] = useState<Practitioner[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const fetchCallCenterAgents = async () => {
    try {
      const searchResult = await medplum.search('PractitionerRole', {
        role: SNOMED_USER_ROLES.CC_MANAGER.code,
        _include: 'PractitionerRole:practitioner',
        _count: 100,
      })
      function isPractitioner(r: Resource | undefined): r is Practitioner {
        return r?.resourceType === 'Practitioner'
      }

      const practitioners: Practitioner[] =
        searchResult.entry
          ?.map((e) => e.resource as Practitioner | undefined)
          .filter(isPractitioner) ?? []
      setCallCenterAgents(practitioners)
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconDownload />,
        title: 'Error',
        message: normalizeErrorString(err) || 'Failed to load call center Agents.',
      })
    }
  }
  useEffect(() => {
    fetchCallCenterAgents()
  }, [])

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === callCenterAgents.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(callCenterAgents.map((a) => a.id!)))
    }
  }

  const handleDeleteAgents = async () => {
    const idsToDelete = agentToDelete ? [agentToDelete.id!] : Array.from(selectedIds)
    if (idsToDelete.length === 0) return

    try {
      for (const id of idsToDelete) {
        const membership = await medplum.searchOne(
          'ProjectMembership',
          `profile=Practitioner/${id}`,
        )
        if (membership?.id) {
          await medplum.deleteResource('ProjectMembership', membership.id)
          if (membership.user?.reference) {
            await medplum.deleteResource(
              'User',
              membership.user.reference.split('/').pop() as string,
            )
          }
        }

        await medplum.deleteResource('Practitioner', id)
      }

      setCallCenterAgents((prev) => prev.filter((a) => !idsToDelete.includes(a.id!)))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        idsToDelete.forEach((id) => next.delete(id))
        return next
      })
      showNotification({
        color: 'green',
        title: 'Success',
        message:
          idsToDelete.length === 1
            ? 'Agent removed successfully.'
            : 'Selected agents removed successfully.',
      })
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: normalizeErrorString(err) || 'Failed to remove agent(s).',
      })
    } finally {
      setDeleteModalOpen(false)
      setAgentToDelete(null)
    }
  }
  return (
    <>
      <Navigation />
      <div className="px-6 py-4">
        <div className="flex w-full items-center gap-4">
          <Title order={2}>Call center agents</Title>
          <Button
            onClick={() => setIsInviteModalOpen(true)}
            variant="subtle"
            leftSection={<IconPlus size={20} />}
          >
            Add Call Center Agent
          </Button>
          {selectedIds.size > 0 && (
            <Button
              onClick={() => {
                setAgentToDelete(null)
                setDeleteModalOpen(true)
              }}
              color="red"
              variant="outline"
              leftSection={<IconTrash size={20} />}
            >
              Remove Selected ({selectedIds.size})
            </Button>
          )}
        </div>
        <Table>
          <Table.Thead className="bg-[#FAFAFA] text-[#7D7987]">
            <Table.Tr className="sticky top-32 z-10 h-[44px] !bg-[#FAFAFA]">
              <Table.Td className="w-10 text-sm">
                <Checkbox
                  checked={
                    callCenterAgents.length > 0 && selectedIds.size === callCenterAgents.length
                  }
                  indeterminate={selectedIds.size > 0 && selectedIds.size < callCenterAgents.length}
                  onChange={toggleSelectAll}
                />
              </Table.Td>
              <Table.Td className="text-sm" key={'Name'}>
                Name
              </Table.Td>
              <Table.Td className="text-sm" key={'Email'}>
                Email
              </Table.Td>
              <Table.Td className="text-sm" key={'Last Updated'}>
                Last Updated
              </Table.Td>
              <Table.Td className="text-sm" key={'Actions'}>
                Actions
              </Table.Td>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {callCenterAgents.map((manager) => (
              <Table.Tr
                key={'call-' + manager.id}
                className="h-[44px] cursor-pointer hover:opacity-85"
              >
                <Table.Td>
                  <Checkbox
                    checked={selectedIds.has(manager.id!)}
                    onChange={() => toggleSelect(manager.id!)}
                  />
                </Table.Td>
                <Table.Td key={'name'}>{formatHumanName(manager.name?.[0])}</Table.Td>
                <Table.Td key={'email'}>
                  {manager?.telecom?.find((contact) => contact.system === 'email')?.value || ''}
                </Table.Td>
                <Table.Td key={'lastUpdated'}>
                  {manager.meta?.lastUpdated
                    ? formatDateTime(manager.meta?.lastUpdated || '', '-', true)
                    : ''}
                </Table.Td>
                <Table.Td key={'actions'}>
                  <Button
                    color="red"
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      setAgentToDelete(manager)
                      setDeleteModalOpen(true)
                    }}
                  >
                    Remove
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Modal
          size="lg"
          zIndex={1000}
          opened={isInviteModalOpen}
          onClose={() => {
            setIsInviteModalOpen(false)
          }}
          withCloseButton={false}
        >
          <InviteCallCenterAgent onClose={() => setIsInviteModalOpen(false)} />
        </Modal>

        <Modal
          opened={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false)
            setAgentToDelete(null)
          }}
          title="Remove Call Center Agent"
          size="lg"
          zIndex={1200}
          centered
        >
          <Stack>
            <Text>
              {agentToDelete ? (
                <>
                  Are you sure you want to remove{' '}
                  <strong>{formatHumanName(agentToDelete.name?.[0])}</strong>?
                </>
              ) : (
                <>
                  Are you sure you want to remove <strong>{selectedIds.size}</strong> selected
                  agent(s)?
                </>
              )}
            </Text>
            <Group mt="md" justify="space-between">
              <Button
                variant="subtle"
                onClick={() => {
                  setDeleteModalOpen(false)
                  setAgentToDelete(null)
                }}
              >
                Cancel
              </Button>
              <Button color="red" onClick={handleDeleteAgents}>
                Remove Agent
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    </>
  )
}
